import type { ComponentPropsWithRef } from 'react';

import { useTranslation } from 'react-i18next';

import type { JobPost } from '~/api/jobPosts/types';

import { useBookmarkJobPost } from '~/api/jobPosts/useBookmarkJobPost';
import { useClickJobPost } from '~/api/jobPosts/useClickJobPost';
import { useIsImpersonating } from '~/api/user';
import { AlertDialog, Avatar, BookmarkButton, Button, Card, Icon, Logo, Menu, RichTextRenderer, StatusTag } from '~/components';
import { SearchParamKeys } from '~/constants/url';
import { useOnCancelledJobPost, useOnFilledJobPost } from '~/pages/JobPosts/JobPostDropdownMenu/JobPostDropdownMenu.hooks';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { clsx } from '~/styles/utils';
import { qs } from '~/utils/searchParams';
import { toast } from '~/utils/toast';

import { type JobPostSpecKey, useJobPostSpecs } from '../JobPost.hooks';
import { JobPostCancelledConfirmDialog } from '../JobPostDropdownMenu/JobPostCancelledConfirmDialog';
import { JobPostFilledConfirmDialog } from '../JobPostDropdownMenu/JobPostFilledConfirmDialog';
import {
  actionsStyle,
  avatarStyle,
  cardContentStyle,
  cardHeaderStyle,
  cardLinkStyle,
  cardStyle,
  clientNameStyle,
  descriptionStyle,
  labelExternalStyle,
  labelStyle,
  logoStyle,
  specificationsStyle,
  specificationStyle,
  specificationTextStyle,
  tagStyle,
  titleStyle,
  titleWrapperStyle,
} from './JobPostCard.css';

type Props = ComponentPropsWithRef<'div'> & {
  jobPost: JobPost;
};

export const JobPostCard = ({ jobPost, ...delegated }: Props) => {
  const { t } = useTranslation(['common', 'jobPosts']);

  const { confirmFilled, filledHandle, isPending: isFilledPending, onFilled } = useOnFilledJobPost(jobPost);
  const { cancelledHandle, confirmCancelled, isPending: isCancelledPending, onCancelled } = useOnCancelledJobPost(jobPost);
  const isImpersonating = useIsImpersonating();

  const clickJobPost = useClickJobPost(jobPost.id);
  const bookmarkMutation = useBookmarkJobPost(jobPost.id);

  const jobPostSpecs = useJobPostSpecs(jobPost);
  const selectedJobPostSpecs: JobPostSpecKey[] = ['price', 'location', 'period', 'experienceLevel'];

  return (
    <>
      <Card.Root {...delegated} className={clsx(cardStyle, delegated.className)}>
        <Card.Link
          aria-label={`${jobPost.clientName} – ${jobPost.title}`}
          className={cardLinkStyle}
          onClick={() => !isImpersonating && clickJobPost.mutate()}
          to={{
            pathname: r(routes.showJobPost, { jobPostId: jobPost.id }),
            search: qs({ [SearchParamKeys.REDIRECT_PATH]: location.pathname + location.search }),
          }}
        />
        <div className={cardContentStyle}>
          <div className={cardHeaderStyle}>
            <Avatar
              aria-hidden
              className={avatarStyle}
              fallback={jobPost.clientName.charAt(0)}
              shape={jobPost.source === 'External' ? 'square' : 'circle'}
              src={jobPost.logoUrl ?? undefined}
            />

            <div className={titleWrapperStyle}>
              <div className={titleStyle}>{jobPost.title}</div>

              <p className={clientNameStyle}>{jobPost.clientName}</p>
            </div>

            {jobPost.userHasCreated && jobPost.status !== 'Open' ? (
              <StatusTag
                className={tagStyle}
                icon={jobPost.status === 'Filled' ? 'Check' : 'CloseSmall'}
                label={t(`jobPosts:fields.status.options.${jobPost.status}`)}
                status={jobPost.status === 'Filled' ? 'success' : 'danger'}
              />
            ) : (
              <div className={actionsStyle}>
                {jobPost.userHasCreated ? (
                  <Menu.Root disabled={isFilledPending || isCancelledPending}>
                    <Menu.Trigger render={<Button aria-label={t('common:actions.openMenu')} icon="MoreVert" />} />

                    <Menu.List>
                      <Menu.LinkItem
                        icon="Edit"
                        to={{
                          pathname: r(routes.editJobPost, { jobPostId: jobPost.id }),
                          search: qs({ [SearchParamKeys.REDIRECT_PATH]: location.pathname + location.search }),
                        }}
                      >
                        {t('jobPosts:contextMenu.actions.edit')}
                      </Menu.LinkItem>

                      <Menu.Item icon="CheckProgress" onClick={onFilled}>
                        {t('jobPosts:contextMenu.actions.filled')}
                      </Menu.Item>

                      <Menu.Item icon="CloseSmall" onClick={onCancelled}>
                        {t('jobPosts:contextMenu.actions.cancelled')}
                      </Menu.Item>
                    </Menu.List>
                  </Menu.Root>
                ) : (
                  <BookmarkButton
                    disabled={bookmarkMutation.isPending}
                    isBookmarked={bookmarkMutation.variables !== undefined ? bookmarkMutation.variables : jobPost.userHasBookmarked}
                    onBookmark={(bookmarked) =>
                      bookmarkMutation.mutate(bookmarked, {
                        onError: () => {
                          bookmarkMutation.reset();
                          toast.danger({ description: t('common:error') });
                        },
                      })
                    }
                  />
                )}
              </div>
            )}
          </div>

          <div aria-hidden className={descriptionStyle}>
            <RichTextRenderer content={jobPost.description} />
          </div>

          <div className={specificationsStyle}>
            {selectedJobPostSpecs.map((key) => (
              <div className={specificationStyle} key={key}>
                {jobPostSpecs[key].value && (
                  <>
                    <Icon aria-hidden={false} aria-label={jobPostSpecs[key].label} name={jobPostSpecs[key].icon} />

                    <p className={specificationTextStyle}>{jobPostSpecs[key].value}</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className={clsx(labelStyle, jobPost.source === 'External' && labelExternalStyle)}>
          {jobPost.source === 'External' ? t('jobPosts:card.postedByCS') : t('jobPosts:card.postedByFreelancer')}

          {jobPost.source === 'External' && <Logo className={logoStyle} variant="symbol" />}
        </div>
      </Card.Root>

      <AlertDialog.Root handle={filledHandle}>
        <JobPostFilledConfirmDialog loading={isFilledPending} onConfirm={confirmFilled} />
      </AlertDialog.Root>

      <AlertDialog.Root handle={cancelledHandle}>
        <JobPostCancelledConfirmDialog loading={isCancelledPending} onConfirm={confirmCancelled} />
      </AlertDialog.Root>
    </>
  );
};
